<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import LogoUpload from '@/components/authentication/company-setup/LogoUpload.vue'

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    mode: {
        type: String,
        default: 'create',
    },
    initialData: {
        type: Object,
        default: () => ({}),
    },
})

const emit = defineEmits(['created', 'back'])
const { t } = useI18n()

const step = ref(1)
const errors = reactive({})

const form = reactive({
    nome: '',
    nomeFantasia: '',
    slug: '',
    logo: '',
})

const isEditMode = computed(() => props.mode === 'edit')

const steps = computed(() => [
    { value: 1, title: t('components.authentication.companySetup.steps.identity') },
    { value: 2, title: t('components.authentication.companySetup.steps.settings') },
])

const slugRules = computed(() => ({
    required: t('components.authentication.companySetup.validation.required'),
    invalid: t('components.authentication.companySetup.validation.slug'),
}))

const clearErrors = () => {
    Object.keys(errors).forEach((key) => delete errors[key])
}

const validateIdentity = () => {
    clearErrors()

    if (!form.nome.trim()) {
        errors.nome = slugRules.value.required
    }

    if (!form.nomeFantasia.trim()) {
        errors.nomeFantasia = slugRules.value.required
    }

    return !errors.nome && !errors.nomeFantasia
}

const validateSettings = () => {
    clearErrors()

    if (!form.slug.trim()) {
        errors.slug = slugRules.value.required
    } else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(form.slug)) {
        errors.slug = slugRules.value.invalid
    }

    if (!form.logo) {
        errors.logo = t('components.authentication.companySetup.validation.logo')
    }

    return !errors.slug && !errors.logo
}

const validateCurrentStep = () => {
    if (step.value === 1) {
        return validateIdentity()
    }

    return validateSettings()
}

const goToStep = (targetStep) => {
    if (targetStep < step.value) {
        clearErrors()
        step.value = targetStep
        return
    }

    if (validateCurrentStep()) {
        step.value = targetStep
    }
}

const nextStep = () => {
    if (validateCurrentStep()) {
        step.value = Math.min(step.value + 1, steps.value.length)
    }
}

const previousStep = () => {
    clearErrors()
    step.value = Math.max(step.value - 1, 1)
}

const goBack = () => {
    clearErrors()
    emit('back')
}

const getLogoBytes = () => {
    return form.logo.includes(',') ? form.logo.split(',').pop() : form.logo
}

const normalizeLogo = (logo) => {
    if (!logo || logo.startsWith('data:')) {
        return logo || ''
    }

    return `data:image/png;base64,${logo}`
}

const fillForm = (data = {}) => {
    form.nome = data.company_name || data.companyName || ''
    form.nomeFantasia = data.business_name || data.businessName || ''
    form.slug = data.auth_name || data.authName || ''
    form.logo = normalizeLogo(data.image_content || data.imageContent || data.logo)
}

watch(
    () => props.initialData,
    (data) => {
        fillForm(data)
    },
    { immediate: true, deep: true }
)

const createCompany = () => {
    if (!validateSettings()) {
        return
    }

    emit('created', {
        authName: form.slug.trim(),
        companyName: form.nome.trim(),
        businessName: form.nomeFantasia.trim(),
        imageContent: getLogoBytes(),
    })
}
</script>

<template>
    <v-dialog :model-value="modelValue" persistent max-width="820" scrollable class="company-setup-dialog">
        <v-card class="company-setup-card bg-white trezo-card">
            <div class="company-setup-card__header">
                <span class="company-setup-card__icon material-symbols-outlined">domain_add</span>
                <div>
                    <h2>{{ $t(isEditMode ? 'components.authentication.companySetup.editTitle' : 'components.authentication.companySetup.title') }}</h2>
                    <p>{{ $t(isEditMode ? 'components.authentication.companySetup.editSubtitle' : 'components.authentication.companySetup.subtitle') }}</p>
                </div>
            </div>

            <v-stepper v-model="step" class="company-stepper" alt-labels>
                <v-stepper-header>
                    <template v-for="item in steps" :key="item.value">
                        <v-stepper-item
                            :value="item.value"
                            :title="item.title"
                            :complete="step > item.value"
                            @click="goToStep(item.value)"
                        />
                        <v-divider v-if="item.value < steps.length" />
                    </template>
                </v-stepper-header>

                <v-stepper-window>
                    <v-stepper-window-item :value="1">
                        <div class="company-step">
                            <div class="company-step__grid company-step__grid--single">
                                <div class="company-field trezo-form-group">
                                    <label for="company-name">{{ $t('components.authentication.companySetup.companyName') }}</label>
                                    <v-text-field
                                        id="company-name"
                                        v-model="form.nome"
                                        rounded="xl"
                                        class="field-default"
                                        hide-details
                                    />
                                    <small v-if="errors.nome">{{ errors.nome }}</small>
                                </div>

                                <div class="company-field trezo-form-group">
                                    <label for="company-trade-name">{{ $t('components.authentication.companySetup.tradeName') }}</label>
                                    <v-text-field
                                        id="company-trade-name"
                                        v-model="form.nomeFantasia"
                                        rounded="xl"
                                        class="field-default"
                                        hide-details
                                    />
                                    <small v-if="errors.nomeFantasia">{{ errors.nomeFantasia }}</small>
                                </div>

                            </div>
                        </div>
                    </v-stepper-window-item>

                    <v-stepper-window-item :value="2">
                        <div class="company-step">
                            <div class="company-step__intro">
                                <h3>{{ $t('components.authentication.companySetup.settingsTitle') }}</h3>
                                <p>{{ $t('components.authentication.companySetup.settingsSubtitle') }}</p>
                            </div>

                            <div class="company-field trezo-form-group">
                                <label for="company-url">{{ $t('components.authentication.companySetup.url') }}</label>
                                <div :class="['company-url-field', { 'company-url-field--error': errors.slug }]">
                                    <span class="company-url-field__affix">https://</span>
                                    <input
                                        id="company-url"
                                        v-model="form.slug"
                                        type="text"
                                        autocomplete="off"
                                        placeholder="minha-empresa"
                                    >
                                    <span class="company-url-field__affix">.hliberty.net</span>
                                </div>
                                <small v-if="errors.slug">{{ errors.slug }}</small>
                            </div>

                            <div class="company-step__intro company-step__intro--logo">
                                <h3>{{ $t('components.authentication.companySetup.logoTitle') }}</h3>
                                <p>{{ $t('components.authentication.companySetup.logoSubtitle') }}</p>
                            </div>

                            <LogoUpload v-model="form.logo" />
                            <small v-if="errors.logo" class="company-step__error">{{ errors.logo }}</small>
                        </div>
                    </v-stepper-window-item>
                </v-stepper-window>
            </v-stepper>

            <div class="company-setup-card__actions">
                <v-btn
                    v-if="step > 1"
                    variant="outlined"
                    class="company-setup-card__secondary"
                    @click="previousStep"
                >
                    {{ $t('common.labels.previous') }}
                </v-btn>
                <v-btn
                    v-else
                    variant="outlined"
                    class="company-setup-card__secondary"
                    @click="goBack"
                >
                    {{ $t('common.labels.back') }}
                </v-btn>

                <v-btn
                    v-if="step < steps.length"
                    class="company-setup-card__primary"
                    @click="nextStep"
                >
                    {{ $t('common.labels.next') }}
                </v-btn>
                <v-btn
                    v-else
                    class="company-setup-card__primary"
                    @click="createCompany"
                >
                    {{ $t(isEditMode ? 'components.authentication.companySetup.saveCompany' : 'components.authentication.companySetup.createCompany') }}
                </v-btn>
            </div>
        </v-card>
    </v-dialog>
</template>

<style lang="scss" scoped>

.dark-theme .company-field label {
    color: var(--whiteColor);
}

.company-setup-card {
    overflow: hidden;
    border-radius: 20px !important;
    box-shadow: 0 28px 80px rgba(38, 9, 68, 0.22);

    &__header {
        display: flex;
        gap: 16px;
        padding: 28px 30px 18px;
        border-bottom: 1px solid #eceef2;

        h2 {
            margin: 0 0 6px;
            font-size: 24px;
            font-weight: 850;
            line-height: 1.2;
        }

        p {
            margin: 0;
            font-size: 14px;
            line-height: 1.55;
        }
    }

    &__icon {
        display: grid;
        flex: 0 0 48px;
        place-items: center;
        width: 48px;
        height: 48px;
        border-radius: 14px;
        background: linear-gradient(135deg, #ffd056 0%, #ffe7389e 100%);
        color: #302424;
        font-size: 27px;
    }

    &__actions {
        display: flex;
        justify-content: space-between;
        gap: 14px;
        padding: 18px 30px 30px;
    }

    &__primary,
    &__secondary {
        min-height: 46px;
        border-radius: 10px;
        font-family: Inter, sans-serif;
        font-weight: 800;
        letter-spacing: 0;
        text-transform: none;
    }

    &__primary {
        background: linear-gradient(135deg, #ffd056 0%, #ffe7389e 100%) !important;
        box-shadow: 0 6px 24px rgba(96, 93, 255, 0.22);
        color: #2c2c2cd0 !important;
    }

    &__secondary {
        border-color: #eceef2;
        color: #605dff;
    }
}

.company-stepper {
    box-shadow: none !important;

    :deep(.v-stepper-header) {
        box-shadow: none;
        padding: 18px 24px 0;
    }

    :deep(.v-stepper-window) {
        margin: 0;
    }
}

.company-step {
    min-height: 360px;
    padding: 26px 30px 10px;

    &__grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 18px;

        &--single {
            grid-template-columns: 1fr;
        }
    }

    &__intro {
        margin-bottom: 18px;

        &--logo {
            margin-top: 24px;
        }

        h3 {
            margin: 0 0 6px;
            color: var(--whiteColor);
            font-size: 18px;
            font-weight: 850;
        }

        p {
            margin: 0;
            color: var(--bodyColor);
            font-size: 13px;
            line-height: 1.55;
        }
    }

    &__error {
        display: block;
        margin-top: 10px;
        color: #dc2626;
        font-size: 12px;
    }
}

.company-field {
    display: grid;
    gap: 8px;

    &--full {
        grid-column: 1 / -1;
    }

    label {
        font-size: 13px;
        font-weight: 700;
    }

    small {
        color: #dc2626;
        font-size: 12px;
    }

    :deep(.v-field) {
        border-radius: 10px;
    }
}

.company-url-field {
    display: grid;
    grid-template-columns: auto minmax(90px, 1fr) auto;
    align-items: stretch;
    min-height: 48px;
    overflow: hidden;
    border: 1px solid #d5d9e2;
    border-radius: 10px;
    background: transparent;
    box-shadow: 0 1px 0 rgba(38, 9, 68, 0.04);
    transition: border-color 0.18s, box-shadow 0.18s;

    &:focus-within {
        border-color: #605dff;
        box-shadow: 0 0 0 3px rgba(96, 93, 255, 0.12);
    }

    &--error {
        border-color: #dc2626;
    }

    &__affix {
        display: inline-flex;
        align-items: center;
        padding: 0 14px;
        background: rgb(71 71 71 / 14%);
        color: var(--bodyColor);
        font-size: 13px;
        font-weight: 800;
        white-space: nowrap;
        user-select: none;
    }

    input {
        min-width: 0;
        border: 0;
        outline: 0;
        padding: 0 14px;
        background: transparent;
        color: var(--blackColor);
        font-family: Inter, sans-serif;
        font-size: 14px;
        font-weight: 700;

        &::placeholder {
            color: #a2a5bd;
            font-weight: 600;
        }
    }
}

:global(.dark-theme) {
    .company-setup-card {
        box-shadow: 0 28px 80px rgba(0, 0, 0, 0.36);

        &__header {
            border-bottom-color: rgba(255, 255, 255, 0.12);
        }

        &__secondary {
            border-color: rgba(255, 255, 255, 0.16);
            color: #aeb4ff;
        }
    }


    .company-step {
        &__intro {

            p {
                color: var(--bodyColor);
            }
        }
    }


    .company-url-field {
        border-color: var(--borderColor);

        &__affix {
            background: var(--borderColor);
        }
    }
}

@media only screen and (max-width: 720px) {
    .company-setup-card {
        &__header,
        &__actions {
            padding-left: 20px;
            padding-right: 20px;
        }
    }

    .company-step {
        min-height: auto;
        padding: 22px 20px 8px;

        &__grid {
            grid-template-columns: 1fr;
        }
    }

    .company-url-field {
        grid-template-columns: 1fr;

        &__affix,
        input {
            min-height: 44px;
        }
    }
}
</style>
